import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { ReportsService } from '../../services/reports.service';

export type StrptDateRange =
	| 'Custom'
	| 'ThisWeek'
	| 'LastMonth'
	| 'ThisMonth'
	| 'ThisYear'
	| '12months'
	| 'ThisQuarter';

export interface StrptSmRow {
	debtorno: string;
	name: string;
	brands: number[];
	other: number;
	lineTotal: number;
}

export interface StrptSmTerritoryBlock {
	territoryId: string;
	territoryName: string;
	rows: StrptSmRow[];
	subtotals: { brands: number[]; other: number; lineTotal: number };
}

@Component({
	selector: 'app-salesman-territory-report',
	templateUrl: './salesman-territory-report.component.html',
	styleUrls: ['./salesman-territory-report.component.scss']
})
export class SalesmanTerritoryReportComponent implements OnInit, OnDestroy {
	/** When true (e.g. dashboard flip panel), hides the report card title row—parent already shows a header. */
	@Input() embedInDashboard = false;

	config: any;
	sending = false;
	metaLoaded = false;

	territories: { territory_id: string; territory_name: string }[] = [];
	locations: { loccode: string; locationname: string }[] = [];
	brandsMeta: { manufacturer_id: number; name: string }[] = [];
	salespeople: any[] = [];

	dateRangeCtrl = new UntypedFormControl('ThisMonth' as StrptDateRange);
	fromDateCtrl = new UntypedFormControl(null);
	toDateCtrl = new UntypedFormControl(null);
	salesmanCtrl = new UntypedFormControl([] as string[]);
	territoryCtrl = new UntypedFormControl('All');
	loccodeCtrl = new UntypedFormControl([] as string[]);
	brandsCtrl = new UntypedFormControl([] as number[]);

	reportPayload: any = null;
	/** Set after a successful run; drives results heading + date pipes */
	reportCriteria: { datefrom: string; dateto: string } | null = null;
	territoryBlocks: StrptSmTerritoryBlock[] = [];
	brandColumns: { brandId: number; label: string; index: number }[] = [];
	grandTotals: { brands: number[]; other: number; lineTotal: number } | null = null;
	resolvedFrom = '';
	resolvedTo = '';

	readonly dateRangeOptions: { value: StrptDateRange; label: string }[] = [
		{ value: 'Custom', label: 'Custom range' },
		{ value: 'ThisWeek', label: 'This week' },
		{ value: 'LastMonth', label: 'Last month' },
		{ value: 'ThisMonth', label: 'This month' },
		{ value: 'ThisYear', label: 'This year (YTD)' },
		{ value: '12months', label: 'Past 12 months' },
		{ value: 'ThisQuarter', label: 'This quarter' }
	];

	metaSub?: Subscription;
	spSub?: Subscription;
	userSub?: Subscription;
	resultSub?: Subscription;

	filename = 'STRPT_Salesman_Territory_';

	constructor(
		public globalSearchService: GlobalSearchService,
		private reportsService: ReportsService,
		private printService: PrintService,
		private locationSvc: Location,
		private cdr: ChangeDetectorRef
	) {
		this.globalSearchService.mesagesallowed.next(false);
		this.globalSearchService.aiDataAvailable$.next(null);
		this.globalSearchService.configsubscription.subscribe((r) => {
			this.config = r;
			this.globalSearchService.getColor();
		});
	}

	ngOnInit(): void {
		this.globalSearchService.getColor();
		const end = new Date();
		const start = new Date(end.getFullYear(), end.getMonth(), 1);
		this.fromDateCtrl.setValue(start);
		this.toDateCtrl.setValue(end);

		this.metaSub = this.reportsService.getStrptSmMeta().subscribe({
			next: (res: any) => {
				if (res && res.success !== false) {
					this.territories = res.territories || [];
					this.locations = res.locations || [];
					const raw = res.brands || [];
					this.brandsMeta = raw.map((b: any) => ({
						manufacturer_id: Number(b.manufacturer_id),
						name: b.name != null ? String(b.name) : ''
					}));
				}
				this.metaLoaded = true;
				this.applySalesmanDefaultFilters();
				this.cdr.markForCheck();
			},
			error: () => {
				this.metaLoaded = true;
				this.applySalesmanDefaultFilters();
			}
		});

		this.spSub = this.reportsService.getSalesPeople().subscribe((rows: any) => {
			this.salespeople = rows || [];
			this.applySalesmanDefaultFilters();
			this.cdr.markForCheck();
		});

		this.userSub = this.globalSearchService.user.subscribe(() =>
			this.applySalesmanDefaultFilters()
		);
	}

	ngOnDestroy(): void {
		this.metaSub?.unsubscribe();
		this.spSub?.unsubscribe();
		this.userSub?.unsubscribe();
		this.resultSub?.unsubscribe();
	}

	/**
	 * For salesman users: all brands, own salesperson code, and default warehouse branch when lists are ready.
	 * Only fills empty controls so returning users keep their choices for this session.
	 */
	private applySalesmanDefaultFilters(): void {
		const auth = this.globalSearchService.user.getValue();
		const u = auth?.user;
		if (!u?.issalesman) {
			return;
		}
		if (!this.metaLoaded || !this.brandsMeta.length) {
			return;
		}

		const brandVals = (this.brandsCtrl.value || []) as number[];
		if (brandVals.length < 1) {
			const allIds = this.brandsMeta
				.map((b) => b.manufacturer_id)
				.filter((id) => Number.isFinite(id) && id > 0);
			if (allIds.length) {
				this.brandsCtrl.setValue(allIds, { emitEvent: false });
			}
		}

		const smCode = String(u.salesman ?? '').trim();
		const smList = (this.salesmanCtrl.value || []) as string[];
		if (smCode && !smList.length && this.salespeople.length) {
			const found = this.salespeople.some((sp) => String(sp?.salesmancode ?? '').trim() === smCode);
			if (found) {
				this.salesmanCtrl.setValue([smCode], { emitEvent: false });
			}
		}

		const locList = (this.loccodeCtrl.value || []) as string[];
		if (!locList.length && this.locations.length) {
			const dl = u.defaultlocation;
			const rawLoc =
				dl && typeof dl === 'object' && dl.loccode != null ? String(dl.loccode).trim() : '';
			if (rawLoc) {
				const ok = this.locations.some((l) => String(l.loccode).trim() === rawLoc);
				if (ok) {
					this.loccodeCtrl.setValue([rawLoc], { emitEvent: false });
				}
			}
		}

		this.cdr.markForCheck();
	}

	back(): void {
		this.locationSvc.back();
	}

	get brandTriggerLabel(): string {
		const ids: number[] = (this.brandsCtrl.value || []) as number[];
		if (!ids.length) {
			return '';
		}
		if (ids.length === this.brandsMeta.length) {
			return `All brands (${ids.length})`;
		}
		const names = ids
			.map((id) => this.brandsMeta.find((b) => b.manufacturer_id === id)?.name)
			.filter(Boolean) as string[];
		if (names.length <= 3) {
			return names.join(', ');
		}
		return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
	}

	get salesmanTriggerLabel(): string {
		const codes: string[] = (this.salesmanCtrl.value || []) as string[];
		if (!codes.length) {
			return '';
		}
		if (this.salespeople.length && codes.length === this.salespeople.length) {
			return `All salespeople (${codes.length})`;
		}
		const names = codes
			.map((c) => this.salespeople.find((sp) => sp.salesmancode === c)?.salesmanname)
			.filter(Boolean) as string[];
		if (names.length <= 3) {
			return names.join(', ');
		}
		return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
	}

	get branchTriggerLabel(): string {
		const codes: string[] = (this.loccodeCtrl.value || []) as string[];
		if (!codes.length) {
			return '';
		}
		if (this.locations.length && codes.length === this.locations.length) {
			return `All branches (${codes.length})`;
		}
		const names = codes
			.map((c) => this.locations.find((l) => l.loccode === c)?.locationname)
			.filter(Boolean) as string[];
		if (names.length <= 3) {
			return names.join(', ');
		}
		return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
	}

	private formatYmd(v: any): string {
		if (!v) {
			return '';
		}
		const d = v instanceof Date ? v : new Date(v);
		if (Number.isNaN(d.getTime())) {
			return '';
		}
		return d.toISOString().split('T')[0];
	}

	runReport(): void {
		const range = (this.dateRangeCtrl.value || 'ThisMonth') as StrptDateRange;
		const brandIds = (this.brandsCtrl.value || [])
			.map((id: any) => Number(id))
			.filter((id: number) => Number.isFinite(id) && id > 0);
		if (brandIds.length < 1) {
			return;
		}
		const salesmen = ((this.salesmanCtrl.value || []) as string[])
			.map((c) => String(c).trim())
			.filter((c) => c.length > 0);
		const loccodes = ((this.loccodeCtrl.value || []) as string[])
			.map((c) => String(c).trim())
			.filter((c) => c.length > 0);

		const payload: any = {
			dateRange: range,
			salesmen,
			loccodes,
			territory: this.territoryCtrl.value || 'All',
			brand_ids: brandIds
		};
		if (range === 'Custom') {
			payload.fromDate = this.formatYmd(this.fromDateCtrl.value);
			payload.toDate = this.formatYmd(this.toDateCtrl.value);
			if (!payload.fromDate || !payload.toDate) {
				return;
			}
		}

		this.reportPayload = payload;
		this.sending = true;
		this.territoryBlocks = [];
		this.brandColumns = [];
		this.grandTotals = null;

		this.resultSub?.unsubscribe();
		this.resultSub = this.reportsService.getStrptSm(payload).subscribe({
			next: (r: any) => {
				this.sending = false;
				if (!r?.success) {
					this.reportCriteria = null;
					this.globalSearchService.aiDataAvailable$.next(null);
					return;
				}
				this.territoryBlocks = r.territories || [];
				this.brandColumns = r.brandColumns || [];
				this.grandTotals = r.grandTotals || null;
				this.resolvedFrom = r.datefrom || r.filters?.datefrom || '';
				this.resolvedTo = r.dateto || r.filters?.dateto || '';
				if (this.resolvedFrom && this.resolvedTo) {
					this.reportCriteria = { datefrom: this.resolvedFrom, dateto: this.resolvedTo };
				}
				this.globalSearchService.aiDataAvailable$.next(r);
				this.cdr.detectChanges();
			},
			error: () => {
				this.sending = false;
			}
		});
	}

	get hasTableData(): boolean {
		return this.territoryBlocks.length > 0 && this.brandColumns.length > 0;
	}

	get totalDataRows(): number {
		return this.territoryBlocks.reduce((n, b) => n + (b.rows?.length || 0), 0);
	}

	get showEmptyAfterRun(): boolean {
		return !!this.reportCriteria && !this.sending && !this.hasTableData;
	}

	money(n: number | undefined | null): string {
		return (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	getSubtitle(): string {
		const p = this.reportPayload;
		if (!p) {
			return '';
		}
		const parts: string[] = [];
		const smList: string[] = Array.isArray(p.salesmen) ? p.salesmen : p.salesman ? [p.salesman] : [];
		if (smList.length) {
			const names = smList
				.map((code) => this.salespeople.find((x) => x.salesmancode === code)?.salesmanname)
				.filter(Boolean);
			if (names.length) {
				parts.push(
					smList.length > 1 ? `Salespeople: ${names.join(', ')}` : `Salesperson: ${names[0]}`
				);
			}
		}
		if (p.territory && p.territory !== 'All') {
			const t = this.territories.find((x) => String(x.territory_id) === String(p.territory));
			if (t) {
				parts.push(`Territory: ${t.territory_name}`);
			}
		}
		const locList: string[] = Array.isArray(p.loccodes)
			? p.loccodes
			: p.loccode && p.loccode !== 'All'
				? [p.loccode]
				: [];
		if (locList.length) {
			const names = locList
				.map((code) => this.locations.find((x) => x.loccode === code)?.locationname)
				.filter(Boolean);
			if (names.length) {
				parts.push(locList.length > 1 ? `Branches: ${names.join(', ')}` : `Branch: ${names[0]}`);
			}
		}
		const ids: number[] = Array.isArray(p.brand_ids) ? p.brand_ids : [];
		if (ids.length) {
			const names = ids
				.map((id) => this.brandsMeta.find((b) => b.manufacturer_id === id)?.name)
				.filter(Boolean);
			if (names.length) {
				parts.push(`Brands: ${names.join(', ')}`);
			}
		}
		return parts.join(' · ');
	}

	generateExportTableHtml(): string {
		if (!this.territoryBlocks.length || !this.brandColumns.length) {
			return '';
		}
		const esc = (s: string) =>
			String(s ?? '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;');

		const thCols = this.brandColumns.map((c) => `<th style="text-align:right">${esc(c.label)}</th>`).join('');
		let html = `<table class="table"><thead><tr>
			<th>Territory / Customer</th><th style="text-align:right">ID</th><th>Customer</th>
			${thCols}<th style="text-align:right">OTHER</th><th style="text-align:right">Total</th>
		</tr></thead><tbody>`;

		const colSpan = 3 + this.brandColumns.length + 2;
		for (const block of this.territoryBlocks) {
			html += `<tr><td colspan="${colSpan}" style="font-weight:bold;background:#f5f5f5">${esc(
				block.territoryName
			)}</td></tr>`;
			for (const row of block.rows) {
				const cells = row.brands.map((v) => `<td style="text-align:right">${this.money(v)}</td>`).join('');
				html += `<tr>
					<td></td>
					<td style="text-align:right">${esc(String(row.debtorno))}</td>
					<td>${esc(row.name)}</td>
					${cells}
					<td style="text-align:right">${this.money(row.other)}</td>
					<td style="text-align:right">${this.money(row.lineTotal)}</td>
				</tr>`;
			}
			const st = block.subtotals;
			const subCells = st.brands.map((v) => `<td style="text-align:right;font-weight:bold">${this.money(v)}</td>`).join('');
			html += `<tr>
				<td></td><td colspan="2" style="font-weight:bold">Subtotal</td>
				${subCells}
				<td style="text-align:right;font-weight:bold">${this.money(st.other)}</td>
				<td style="text-align:right;font-weight:bold">${this.money(st.lineTotal)}</td>
			</tr>`;
		}

		if (this.grandTotals) {
			const gt = this.grandTotals;
			const gtCells = gt.brands.map((v) => `<td style="text-align:right;font-weight:bold">${this.money(v)}</td>`).join('');
			html += `<tr>
				<td></td><td colspan="2" style="font-weight:bold">Grand total</td>
				${gtCells}
				<td style="text-align:right;font-weight:bold">${this.money(gt.other)}</td>
				<td style="text-align:right;font-weight:bold">${this.money(gt.lineTotal)}</td>
			</tr>`;
		}

		html += '</tbody></table>';
		return html;
	}

	exportPdf(): void {
		if (!this.territoryBlocks.length || !this.brandColumns.length) {
			return;
		}
		this.sending = true;
		const now = new Date();
		const encoded = this.globalSearchService.base64encode(this.generateExportTableHtml());
		const data = {
			content: encoded,
			filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
			title: `Salesman Territory Report ${this.resolvedFrom} – ${this.resolvedTo} @ ${now.toLocaleString()}`,
			subtitle: this.getSubtitle()
		};
		this.printService.pdf(data).subscribe({
			next: (result: any) => {
				this.sending = false;
				this.globalSearchService.downloadPdf(result.content, data.filename);
			},
			error: () => {
				this.sending = false;
			}
		});
	}

	exportXls(): void {
		if (!this.territoryBlocks.length || !this.brandColumns.length) {
			return;
		}
		this.sending = true;
		const now = new Date();
		const encoded = this.globalSearchService.base64encode(this.generateExportTableHtml());
		const data = {
			content: encoded,
			filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
			title: `Salesman Territory Report ${this.resolvedFrom} ${this.resolvedTo} @ ${now.toLocaleString()}`,
			subtitle: this.getSubtitle()
		};
		this.printService.xls(data).subscribe({
			next: (result: any) => {
				this.sending = false;
				this.globalSearchService.downloadXls(result.content, data.filename);
			},
			error: () => {
				this.sending = false;
			}
		});
	}
}
